import * as _ from "lodash";
import * as React from "react";
import * as RC from "recharts";

import * as S from "@styled";

import { IRuleData, IRuleDataPoint } from "../shared/model";
import { IAxisTickProps } from "shared/model/Chart";
import useChartZoom, { IChartState, IYAxisDomain } from "shared/hooks/useChartZoom";
import useUserTheme from "shared/styles/hooks/useUserTheme";
import Time from "shared/Time";

const XAxisTick = (props: IAxisTickProps) => {
    const date = Time.toLocalDate(new Date(props.payload.value).toISOString());
    const time = Time.toLocalFormat(new Date(props.payload.value).toISOString(), "HH:mm");
    return <g style={{ ...S.GraphFontStyles(props.theme), userSelect: "none" }} transform={`translate(${props.x},${props.y})`}>
        <text x={0} y={0} textAnchor="middle">
            <tspan x="0" dy="1em">{date}</tspan>
            <tspan x="0" dy="1.4em">{time}</tspan>
        </text>
    </g>
}

const RuleRowChart: React.FC<{ rule: IRuleData }> = ({ rule }) => {
    const [theme] = useUserTheme();
    const chartTimes = React.useMemo(() => rule.chartData.map((chart) => chart.x), [rule.chartData]);

    const initialState: IChartState = {
        left: "dataMin",
        right: "dataMax",
        top: "dataMax+5",
        bottom: "dataMin-5",
        visibleData: chartTimes ?? [],
    };

    const getAxisYDomain = (from: number, to: number, offset: number): IYAxisDomain => {
        const startIndex = rule.chartData.findIndex((r) => r.x === from);
        const endIndex = rule.chartData.findIndex((r) => r.x === to);

        // Items to show between left and right
        const refData = rule.chartData.slice(startIndex, endIndex + 1) ?? [];

        if (refData.length === 0) {
            return ["dataMin", "dataMax", []];
        }

        // highest point on the Y axis
        const top = Math.max(...refData.map((dataPoint) => {
            return Math.max(dataPoint.alert ?? 0, dataPoint.series ?? 0)
        }))

        // lowest point on the Y axis
        const bottom = Math.min(...refData.map((dataPoint) => {
            return Math.min(dataPoint.alert ?? top, dataPoint.series ?? top)
        }))

        return [(bottom) - offset, (top ?? 0) + offset, refData.map((chart) => chart.x)];
    };

    const {
        state, mouseMovedRefArea, mouseDownRefArea, updateCurrentMouseRef,
        handleMouseDown, handleMouseMove, handleScrollZoom, handleShowNewer,
        handleShowOlder, handleZoomSelection, zoomOut
    } = useChartZoom(initialState, chartTimes, getAxisYDomain);

    const visibleData = React.useMemo(() => rule.chartData.filter((f) => state.visibleData.some((s) => s === f.x)), [rule.chartData, state.visibleData]);

    const handleTooltipContentFormat = (value: number, name: "Rule Value" | "Alert", props: { payload: IRuleDataPoint }) => {
        if (name === "Rule Value") {
            return value;
        }
        if (name === "Alert") {
            // eslint-disable-next-line react/prop-types
            return props.payload.alertLabel;
        }
    }

    const handleShorterTooltipLabelDateFormat = (label: number) => {
        return Time.toDetailedLocalTime(new Date(label).toISOString());
    }

    const opts: AddEventListenerOptions & EventListenerOptions = { passive: false };

    const enableScroll = () => {
        document.removeEventListener('wheel', handlePreventDefault, opts)
    }
    const disableScroll = () => {
        document.addEventListener('wheel', handlePreventDefault, opts)
    }

    const handlePreventDefault = React.useCallback((e: WheelEvent) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
    }, [rule.id]) // Use static dependency to never re-create the function

    const isZoomedIn = rule.chartData.length !== state.visibleData.length;

    const showLeftPan = isZoomedIn && rule.chartData[0].x !== state.visibleData[0];
    const showRightPan = isZoomedIn && rule.chartData[rule.chartData.length - 1].x !== state.visibleData[state.visibleData.length - 1];

    if (rule.chartData == null || _.isEmpty(rule.chartData)) {
        return <div>No series and or value/alert data received yet.</div>;
    }

    return (
        <div onWheel={handleScrollZoom} onMouseEnter={disableScroll} onMouseLeave={enableScroll}>
            <S.FlexContainer style={{ justifyContent: "flex-end" }}>
                <S.MiniButton variant={S.ButtonType.bordered} style={{ marginRight: 23, marginBottom: 5, visibility: isZoomedIn ? "visible" : "hidden" }} onClick={zoomOut}>Zoom out</S.MiniButton>
            </S.FlexContainer>
            <S.FlexContainer noWrap={true} asRow={true}>
                <i
                    title="Show older"
                    style={{
                        cursor: "pointer",
                        visibility: showLeftPan ? "visible" : "hidden",
                        top: 105,
                        position: "relative"
                    }}
                    onClick={handleShowOlder}
                    className="ci-chevron_big_left"></i>
                <RC.ResponsiveContainer height={300}>
                    <RC.ComposedChart
                        data={visibleData}
                        margin={{
                            top: 10,
                            right: 0,
                            left: 0,
                            bottom: 0,
                        }}
                        onMouseDown={handleMouseDown}
                        onMouseMove={handleMouseMove}
                        onMouseLeave={() =>
                            updateCurrentMouseRef(undefined)
                        }
                        onMouseUp={handleZoomSelection}
                    >
                        <RC.CartesianGrid strokeDasharray="3 3" />
                        <RC.XAxis domain={[state.left, state.right]} allowDataOverflow={true} scale="time" type="number" dataKey="x" tickMargin={10} tick={(tickProps) => <XAxisTick {...tickProps} theme={theme} />} />
                        <RC.YAxis type="number" yAxisId="1" allowDataOverflow={true} domain={[state.bottom, state.top]} style={{ userSelect: "none" }} tickMargin={15} tickCount={6} tick={true} />
                        <RC.Tooltip

                            labelStyle={S.TooltipLabelStyles}
                            contentStyle={S.TooltipContentStyles}
                            itemStyle={S.TooltipLabelStyles}
                            formatter={handleTooltipContentFormat}
                            labelFormatter={handleShorterTooltipLabelDateFormat}
                        />
                        <RC.Legend wrapperStyle={{
                            paddingTop: "30px"
                        }} />

                        <RC.Line isAnimationActive={false} name="Rule Value" yAxisId="1" type="monotone" dataKey="series" stroke={S.Colors.purplePrimary} strokeWidth={1} fill={S.Colors.purplePrimary} />
                        <RC.Scatter isAnimationActive={false} name="Alert" dataKey="alert" yAxisId="1" stroke={S.Colors.warningDark} strokeWidth={1} fill={S.Colors.warningDark} />
                        {mouseDownRefArea && mouseMovedRefArea ? (
                            <RC.ReferenceArea
                                x1={mouseDownRefArea}
                                x2={mouseMovedRefArea}
                                yAxisId="1"
                                strokeOpacity={0.3}
                            />
                        ) : null}
                    </RC.ComposedChart>
                </RC.ResponsiveContainer>
                <i
                    title="Show newer"
                    style={{
                        cursor: "pointer",
                        visibility: showRightPan ? "visible" : "hidden",
                        top: 105,
                        position: "relative",
                        marginLeft: 5
                    }}
                    onClick={handleShowNewer}
                    className="ci-chevron_big_right"></i>
            </S.FlexContainer>
        </div>
    );
};

export default RuleRowChart;
